/**
 * Pure sort + filter helpers for the model picker. Lives outside the
 * component so we can unit-test the rules without mounting React.
 */

import type { ProviderCatalogModel } from '../services/providerService';
import type { ModelOption } from '../labs/shared/agent-bar/modelOptions';

/** Convert a BE catalog model into a picker `ModelOption`. Preserves
 *  releaseDate + tags via the option's `source` group label below. */
export function catalogModelToOption(m: ProviderCatalogModel): ModelOption {
  return {
    value: m.id,
    label: m.displayName || m.id,
    // Source-group label: prefer "newest" if tagged, then "legacy", else
    // "catalog" (the existing default the agent-bar code keys off).
    source: m.tags?.includes('newest')
      ? 'catalog'
      : m.tags?.includes('legacy')
        ? 'catalog'
        : 'catalog',
  };
}

/** Hide legacy/deprecated entries from a model list. Returns the list
 *  verbatim when `hideLegacy` is false. */
export function applyLegacyFilter(
  models: ProviderCatalogModel[],
  hideLegacy: boolean,
): ProviderCatalogModel[] {
  if (!hideLegacy) return models;
  return models.filter(m => !(m.tags ?? []).some(t => t === 'legacy' || t === 'deprecated'));
}

/**
 * Sort by release date, newest first. Models without a releaseDate sort
 * to the bottom (their relative order is preserved — stable).
 */
export function sortByReleaseDateNewestFirst(
  models: ProviderCatalogModel[],
): ProviderCatalogModel[] {
  // Index the originals so we can break ties stably.
  const indexed = models.map((m, i) => ({ m, i }));
  indexed.sort((a, b) => {
    const ad = a.m.releaseDate ? Date.parse(a.m.releaseDate) : NaN;
    const bd = b.m.releaseDate ? Date.parse(b.m.releaseDate) : NaN;
    const aHas = Number.isFinite(ad);
    const bHas = Number.isFinite(bd);
    if (aHas && bHas) {
      if (ad !== bd) return bd - ad; // newer (larger ms) first
      return a.i - b.i;
    }
    if (aHas) return -1;
    if (bHas) return 1;
    return a.i - b.i;
  });
  return indexed.map(x => x.m);
}

/**
 * One-shot pipeline: filter then sort. Encapsulates the two operations
 * so consumers don't need to remember the right order.
 */
export function filteredSortedModels(
  models: ProviderCatalogModel[],
  options: { hideLegacy: boolean },
): ProviderCatalogModel[] {
  return sortByReleaseDateNewestFirst(applyLegacyFilter(models, options.hideLegacy));
}

/**
 * Group label for the sorted/filtered model list. Falls back to a year
 * bucket when releaseDate is present ("Released 2026"), then "Older"
 * for everything else.
 */
export function groupLabelForModel(m: ProviderCatalogModel): string {
  const tags = m.tags ?? [];
  if (tags.includes('newest')) return 'Newest (2026)';
  if (tags.includes('deprecated')) return 'Deprecated';
  if (tags.includes('legacy')) return 'Legacy';
  if (m.releaseDate) {
    const ts = Date.parse(m.releaseDate);
    if (Number.isFinite(ts)) {
      const year = new Date(ts).getUTCFullYear();
      return `Released ${year}`;
    }
  }
  return 'Other';
}
